import {isObject} from '@/lib/data/Validations/utils/Utils'
import {checkVAT, countries} from 'jsvat-next'
import getValueWithDotNotation from 'get-value'
import setValueWithDotNotation from 'set-value'
import isDesktopApp from '@/desktop-app/isDesktopApp.js'
import desktopApp from '@/desktop-app/desktopApp.js'
import ipcCommandsEnum from '@/desktop-app/ipcCommandsEnum.js'
import toGreeklish from '../../filters/string/toGreeklish.js'
import i18n from '@/lang/lang.js'

const SECONDS_PER_DAY = 86400
const HOURS_PER_DAY = 24

/**
 *
 * @param items {Array}
 * @param fieldName {String}
 * @returns {Number}
 */
export const sumArrObjProp = (items, fieldName = 'Amount') => {
  const itemsArray = Array.isArray(items) ? items : []
  const total = itemsArray.reduce((total, item) => total + (parseFloat(item[fieldName] || 0)), 0) || 0
  return toFloat(total)
}


/**
 *
 * @param items {Array}
 * @returns {Number}
 */
export const sumArr = (items) => {
  const itemsArray = Array.isArray(items) ? items : []
  const total = itemsArray.reduce((total, item) => total + (parseFloat(item) || 0), 0) || 0
  return toFloat(total)
}

/**
 *
 * @param item
 * @param fieldName
 * @returns {number}
 */
export const quantityFromItem = (item, fieldName = 'Quantity') => {
  return toInt(item[fieldName])
}

/**
 *
 * @param item
 * @param fieldName
 * @returns {number}
 */
export const priceFromItem = (item, fieldName = 'Price') => {
  return toFloat(item[fieldName])
}

/**
 *
 * @param val
 * @returns {number}
 */
export const toFloat = (val) => {
  return parseFloat(parseFloat(val || 0).toFixed(2))
}

/**
 *
 * @param val
 * @returns {number}
 */
export const toInt = (val) => {
  return parseInt(val || 0)
}

/**
 *
 * @param valueInt
 * @param nearestInt
 * @returns {number}
 */
export const roundIntToNearest = (valueInt, nearestInt) => Math.round(toInt(valueInt) / toInt(nearestInt)) * toInt(nearestInt)

/**
 *
 * @param valueInt
 * @param nearestInt
 * @returns {number}
 */
export const floorIntToNearest = (valueInt, nearestInt) => Math.floor(toInt(valueInt) / toInt(nearestInt)) * toInt(nearestInt)

export const roundDateToNearest = (minutes, d = new Date()) => {
  const ms = 1000 * 60 * minutes // convert minutes to ms
  return new Date(Math.round(d.getTime() / ms) * ms)
}

/**
 *
 * @param obj
 * @returns {{}}
 */
export const dotNotationToObj = (obj) => {
  const result = {}

  // For each object path (property key) in the object
  for (const objectPath in obj) {
    // Split path into component parts
    const parts = objectPath.split('.')

    // Create sub-objects along path as needed
    let target = result
    while (parts.length > 1) {
      const part = parts.shift()
      target = target[part] = target[part] || {}
    }

    // Set value at end of path
    target[parts[0]] = obj[objectPath]
  }

  return result
}

/**
 *
 * @param obj
 * @param res
 * @param current
 * @returns {{}}
 */
export const objToDotNotation = (obj, res = {}, current = '') => {
  for (const key in obj) {
    const value = obj[key]
    const newKey = (current ? current + '.' + key : key) // joined key with dot
    if (value && typeof value === 'object') {
      objToDotNotation(value, res, newKey) // it's a nested object, so do it again
    } else {
      res[newKey] = value // it's not an object, so set the property
    }
  }
  return res
}

/**
 *
 * @param obj {Object}: The object to set value on
 * @param path {String|Array}: The path of the property to set.
 * @param options {Object}: See all available options https://github.com/jonschlinkert/get-value#options
 * @returns {*}
 */
export const getPropValueWithDotNotation = (obj, path, options) => {
  return getValueWithDotNotation(obj, path, options)
}
/**
 *
 * @param obj {Object}: The object to set value on
 * @param path {String|Array}: The path of the property to set.
 * @param value {Any}: The value to set on obj[prop]
 * @param options {Object}: See all available options https://github.com/jonschlinkert/set-value#options
 */
export const setPropValueWithDotNotation = (obj, path, value, options) => {
  setValueWithDotNotation(obj, path, value)
}

/**
 *
 * @param array
 * @param keyName
 * @returns Array
 */
export const arrayUniqueByObjectKey = (array = [], keyName = '') => [...new Map(array.map(item => [item[keyName], item])).values()]

/**
 *
 * @param obj
 * @param keyName
 * @returns {{[p: string]: unknown}}
 */
export const filterObjectByKeyIncludes = (obj = {}, keyName = '') => {
  return Object.fromEntries(Object.entries(obj).filter(([key]) => key.includes(keyName)))
}
/**
 *
 * @param obj
 * @param keyName
 * @returns {{[p: string]: unknown}}
 */
export const filterObjectByKeyStartsWith = (obj = {}, keyName = '') => {
  return Object.fromEntries(Object.entries(obj).filter(([key]) => key.startsWith(keyName)))
}

/**
 *
 * @param email
 * @returns {*}
 */
export const emailIsGuest = (email) => {
  return email?.includes('guest.getfoodpro.gr')
}

/**
 * Deep merge objects.
 *
 * @param sources
 * @returns {Object}
 */
export function mergeDeep(...sources) {
  const target = {}
  if (!sources.length) {
    return target
  }

  while (sources.length > 0) {
    const source = sources.shift()
    if (isObject(source)) {
      for (const key in source) {
        if (isObject(source[key])) {
          target[key] = mergeDeep(target[key], source[key])
        } else {
          Object.assign(target, {[key]: source[key]})
        }
      }
    }
  }
  return target
}

/**
 * Flatten a deep object into a one level object with its path as key
 *
 * @param {object} object - The object to be flattened
 * @param {string} separator - the separator used, default '.'
 *
 * @return {object}        - The resulting flat object
 */
export function flattenObj(object, separator = '.') {
  return Object.assign({}, ...function _flatten(objectBit, path = '') { //spread the result into our return object
    return [].concat( //concat everything into one level
      ...Object.keys(objectBit).map( //iterate over object
        key => typeof objectBit[key] === 'object' ? //check if there is a nested object
          _flatten(objectBit[key], `${path}${separator}${key}`) : //call itself if there is
          ({[`${path}${separator}${key}`]: objectBit[key]}) //append object with its path as key
      )
    )
  }(object))
}

export const clone = (obj) => {
  return JSON.parse(JSON.stringify(obj))
}

export const sortBy = (array, property, order = 'asc') => {
  const isAscending = order.toLowerCase() === 'asc'

  return array.sort((a, b) => {
    if (a[property] > b[property]) {
      return isAscending ? 1 : -1
    }

    if (a[property] < b[property]) {
      return isAscending ? -1 : 1
    }

    return 0
  })
}

export const slugify = (text) => {
  return toGreeklish(String(text)).toLowerCase()
    .replace(/[àÀáÁâÂãäÄÅåª]+/g, 'a') // Special Characters #1
    .replace(/[èÈéÉêÊëË]+/g, 'e') // Special Characters #2
    .replace(/[ìÌíÍîÎïÏ]+/g, 'i') // Special Characters #3
    .replace(/[òÒóÓôÔõÕöÖº]+/g, 'o') // Special Characters #4
    .replace(/[ùÙúÚûÛüÜ]+/g, 'u') // Special Characters #5
    .replace(/[ýÝÿŸ]+/g, 'y') // Special Characters #6
    .replace(/[ñÑ]+/g, 'n') // Special Characters #7
    .replace(/[çÇ]+/g, 'c') // Special Characters #8
    .replace(/[ß]+/g, 'ss') // Special Characters #9
    .replace(/[Ææ]+/g, 'ae') // Special Characters #10
    .replace(/[Øøœ]+/g, 'oe') // Special Characters #11
    .replace(/[%]+/g, 'pct') // Special Characters #12
    .replace(/\s+/g, '-') // Replace spaces with -
    .replace(/[^\w\-]+/g, '') // Remove all non-word chars
    .replace(/\-\-+/g, '-') // Replace multiple - with single -
    .replace(/^-+/, '') // Trim - from start of text
    .replace(/-+$/, '') // Trim - from end of text
}

export const syntaxHighlight = (json) => {
  json = JSON.stringify(json, undefined, 4) || ''
  json = json.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
  json = json.replace(/("(\\u[a-zA-Z0-9]{4}|\\[^u]|[^\\"])*"(\s*:)?|\b(true|false|null)\b|-?\d+(?:\.\d*)?(?:[eE][+-]?\d+)?)/g, function (match) {
    let cls = 'number'
    if (/^"/.test(match)) {
      if (/:$/.test(match)) {
        cls = 'key'
      } else {
        cls = 'string'
      }
    } else if (/true|false/.test(match)) {
      cls = 'boolean'
    } else if (/null/.test(match)) {
      cls = 'null'
    }
    return '<span class="' + cls + '">' + match + '</span>'
  })

  return json
}

/**
 *
 * @param hex
 * @param bw
 * @returns {string|string}
 */
export const invertColor = (hex, bw) => {
  if (!hex) return '#FFFFFF'

  if (hex.indexOf('#') === 0) {
    hex = hex.slice(1)
  }
  // convert 3-digit hex to 6-digits.
  if (hex.length === 3) {
    hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2]
  }
  if (hex.length !== 6) {

    console.warn('Invalid HEX color.')
    return '#FFFFFF'
  }
  let r = parseInt(hex.slice(0, 2), 16)
  let g = parseInt(hex.slice(2, 4), 16)
  let b = parseInt(hex.slice(4, 6), 16)
  if (bw) {
    // http://stackoverflow.com/a/3943023/112731
    return (r * 0.299 + g * 0.587 + b * 0.114) > 186
      ? '#323232'
      : '#FFFFFF'
  }
  // invert color components
  r = (255 - r).toString(16)
  g = (255 - g).toString(16)
  b = (255 - b).toString(16)
  // pad each with zeros and return
  return '#' + this.padZero(r) + this.padZero(g) + this.padZero(b)
}

/**
 *
 * @param countryCode
 * @param vatNumber
 * @param allowUnsupportedCountries
 * @returns {boolean}
 */
export const validateVAT = (countryCode, vatNumber, allowUnsupportedCountries = true) => {
  const result = checkVAT(countryCode + vatNumber, countries)
  if (!result.isSupportedCountry) return allowUnsupportedCountries

  return result.isSupportedCountry && result.isValid && result.isValidFormat
}

export const reloadApp = () => {
  if (isDesktopApp()) {
    const desktopBridge = desktopApp()
    desktopBridge.send(ipcCommandsEnum.RetryLoadMainApp)
  } else {
    const host = window.location.protocol + '//' + window.location.hostname + (window.location.port ? ':' + window.location.port : '')
    window.location.replace(host)
  }
}

export const formatVuetifyDate = (date) => {
  if (!date) return ''

  const [year, month, day] = date.split('-')
  return `${day}/${month}/${year}`
}

export const parseVuetifyDate = (date) => {
  if (!date) return ''

  const [day, month, year] = date.split('/')
  return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`
}

export const isAndroid = () => {
  return (/android/i.test(navigator.userAgent))
}

export const isIos = () => {
  return (/iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1))
}

export const isMobile = () => {
  return isAndroid() || isIos()
}

export const isDesktop = () => {
  return !isMobile() || isDesktopApp()
}

export const getTimestampString = (highPrecision = false) => {
  return highPrecision ? String(Date.now() + '.' + performance.now()) : String(Date.now())
}

export const splitDateTimeStrToObj = (str) => {
  const [d, t] = String(str).split(' ')
  return {
    Date: d || '',
    Time: t || ''
  }
}

/**
 *
 * @param date
 * @param months
 * @returns {Date}
 *
 * dateAddMonths(new Date('2026-01-31'), 2);
 * → March 31, 2026 ✓
 */
export const dateAddMonths = (date, months) => {
  const result = new Date(date)
  const targetMonth = result.getMonth() + months

  result.setMonth(targetMonth)

  // If the month overflowed, we went too far — clamp to last day
  // of the intended month
  if (result.getMonth() !== ((targetMonth % 12) + 12) % 12) {
    result.setDate(0) // setDate(0) = last day of previous month
  }

  return result
}

/**
 *
 * @param date
 * @param months
 * @returns {Date}
 *
 * dateSubtractMonths(new Date('2026-05-31'), 2);
 * → March 31, 2026 ✓
 */
export const dateSubtractMonths = (date, months) => {
  return dateAddMonths(date, -months)
}

/**
 *
 * @param date
 * @param days
 * @returns {Date}
 *
 * dateAddDays(new Date('2026-01-31'), 2);
 * → Feb 2, 2026 ✓
 */
export const dateAddDays = (date, days) => {
  const result = new Date(date)
  const target = result.getDate() + days
  result.setDate(target)

  return result
}

/**
 *
 * @param date
 * @param days
 * @returns {Date}
 *
 * dateSubtractMonths(new Date('2026-05-31'), 2);
 * → Jan 29, 2026 ✓
 */
export const dateSubtractDays = (date, days) => {
  return dateAddDays(date, -days)
}

/**
 * Convert seconds to HH:MM:SS
 * If seconds exceeds 24 hours, hours will be greater than 24 (30:05:10)
 *
 * @param {number} seconds
 * @returns {string}
 */
export const secondsToHms = seconds => {
  const days = Math.floor(seconds / SECONDS_PER_DAY)
  const remainderSeconds = seconds % SECONDS_PER_DAY
  const hms = new Date(remainderSeconds * 1000).toISOString().substring(11, 19)
  return hms.replace(/^(\d+)/, h => `${Number(h) + days * HOURS_PER_DAY}`.padStart(2, '0'))
}


export const getLocaleForReports = () => {
  const userLocale = window?.Vue?.$i18n?.locale || import.meta.env.VITE_I18N_LOCALE || import.meta.env.VITE_I18N_FALLBACK_LOCALE || 'el'
  const usePrintDefaultLanguage = window?.appConfig?.LOCATION_DATA?.UsePrintDefaultLanguage ?? false
  const printDefaultLanguage = window?.appConfig?.LOCATION_DATA?.PrintDefaultLanguage || 12
  const printDefaultLanguageLocale = i18n.languages.find(l => parseInt(l.id) === parseInt(printDefaultLanguage))?.locale || userLocale

  return usePrintDefaultLanguage ? printDefaultLanguageLocale : userLocale
}
